import React, { useCallback, useRef, useState } from "react";
import { Upload, X, Loader2, Image as ImageIcon, AlertCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { ConfirmModal } from "@/components/AppDialogs";

const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPTED = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

export default function ImageUpload({
  value,          // { url, public_id } or a legacy string URL
  onChange,       // (asset) => void — asset = { url, public_id } or null
  folder = "milkado/products/",
  label = "Image",
  className = "",
  testid = "image-upload",
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const currentUrl = typeof value === "string" ? value : value?.url || "";
  const currentPid = typeof value === "string" ? null : value?.public_id || null;
  const isLegacy = typeof value === "string" && !!value;

  const upload = useCallback(async (file) => {
    setError("");
    if (!ACCEPTED.includes(file.type)) {
      setError("Format non supporté. JPG / PNG / WebP uniquement.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(`Fichier trop lourd (${(file.size / 1024 / 1024).toFixed(1)} Mo). Max 5 Mo.`);
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("folder", folder);
      const { data } = await api.post("/admin/uploads", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange({ url: data.url, public_id: data.public_id });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message || "Échec de l'envoi.");
    } finally {
      setBusy(false);
    }
  }, [folder, onChange]);

  const remove = useCallback(() => {
    setRemoveConfirmOpen(true);
  }, []);

  const performRemove = useCallback(async () => {
    setRemoveConfirmOpen(false);
    if (!currentPid) {
      onChange(null);
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/uploads/delete", { public_id: currentPid });
    } catch (err) {
      console.warn("Image delete failed:", err?.message || err);
    } finally {
      onChange(null);
      setBusy(false);
    }
  }, [currentPid, onChange]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  };

  return (
    <div className={className} data-testid={testid}>
      {label && (
        <p className="text-xs font-bold uppercase tracking-widest text-[#5C4664] mb-1.5">{label}</p>
      )}

      {currentUrl ? (
        <div className="relative block w-full max-w-[240px] group">
          <img
            src={currentUrl}
            alt="Aperçu"
            className="w-full max-w-[240px] aspect-square object-cover rounded-2xl border border-[#F2E2EC]"
            data-testid={`${testid}-preview`}
          />
          {isLegacy && (
            <span className="absolute top-2 left-2 bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full">
              URL héritée
            </span>
          )}
          <button
            type="button"
            onClick={remove}
            disabled={busy}
            data-testid={`${testid}-remove`}
            aria-label="Retirer l'image"
            className="absolute top-2 right-2 w-8 h-8 rounded-full bg-red-600 hover:bg-red-700 text-white shadow-lg flex items-center justify-center disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
          </button>
        </div>
      ) : (
        <label
          onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          data-testid={`${testid}-dropzone`}
          className={`flex flex-col items-center justify-center rounded-2xl border-2 border-dashed p-6 cursor-pointer transition-colors ${
            dragOver
              ? "border-[#D81B8C] bg-[#FCE7F3]"
              : "border-[#F2E2EC] bg-white hover:border-[#D81B8C]/60"
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED.join(",")}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload(f);
              e.target.value = ""; // allow re-selecting the same file after remove
            }}
            className="hidden"
            data-testid={`${testid}-input`}
          />
          {busy ? (
            <>
              <Loader2 className="w-6 h-6 animate-spin text-[#D81B8C]" />
              <p className="text-xs text-[#5C4664] mt-2">Envoi en cours…</p>
            </>
          ) : (
            <>
              <div className="w-11 h-11 rounded-full bg-[#FCE7F3] text-[#D81B8C] flex items-center justify-center">
                <Upload className="w-5 h-5" />
              </div>
              <p className="text-sm font-semibold text-[#2A0E3C] mt-2">
                Glissez une image ici ou cliquez
              </p>
              <p className="text-[11px] text-[#5C4664] mt-1">
                JPG / PNG / WebP — max 5 Mo
              </p>
            </>
          )}
        </label>
      )}

      {error && (
        <p data-testid={`${testid}-error`} className="mt-2 text-xs text-red-600 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> {error}
        </p>
      )}

      <ConfirmModal
        open={removeConfirmOpen}
        onClose={() => setRemoveConfirmOpen(false)}
        onConfirm={performRemove}
        title="Supprimer cette image ?"
        description="L'image sera retirée du produit et effacée du stockage."
        confirmLabel="Supprimer"
        destructive
        testId={`${testid}-remove-confirm`}
      />
    </div>
  );
}

/** Grid variant for uploading multiple additional images (up to `max`). */
export function ImageUploadGrid({ value, onChange, max = 5, folder = "milkado/products/", testid = "image-upload-grid" }) {
  const items = Array.isArray(value) ? value : [];
  const setAt = (idx, asset) => {
    const next = [...items];
    if (asset) next[idx] = asset;
    else next.splice(idx, 1);
    onChange(next.filter(Boolean));
  };
  const canAdd = items.length < max;
  return (
    <div data-testid={testid}>
      <p className="text-xs font-bold uppercase tracking-widest text-[#5C4664] mb-2">
        Images supplémentaires <span className="text-[10px] normal-case">({items.length}/{max})</span>
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {items.map((it, idx) => (
          <ImageUpload
            key={it.public_id || it.url || idx}
            value={it}
            onChange={(a) => setAt(idx, a)}
            folder={folder}
            label=""
            testid={`${testid}-${idx}`}
          />
        ))}
        {canAdd && (
          <ImageUpload
            value={null}
            onChange={(a) => a && setAt(items.length, a)}
            folder={folder}
            label=""
            testid={`${testid}-add`}
          />
        )}
      </div>
    </div>
  );
}
